import {
  ArrowRight,
  BadgeCheck,
  ChevronRight,
  Clock,
  LayoutGrid,
  Store,
  Truck,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useSession } from "@/features/auth";
import { FavoriteButton, useFavoriteIdSet } from "@/features/favorites";
import {
  useCategories,
  usePopular,
  useProducts,
} from "@/shared/api/generated/catalog-controller/catalog-controller";
import { useContractsSummary } from "@/shared/api/generated/contract-controller/contract-controller";
import type { ProductCardResponse } from "@/shared/api/generated/iSHONCHPlatformAPI.schemas";
import heroAppliances from "@/shared/assets/hero-appliances.webp";
import heroShape1 from "@/shared/assets/hero-shape-1.svg";
import heroShape2 from "@/shared/assets/hero-shape-2.svg";
import heroShape3 from "@/shared/assets/hero-shape-3.svg";
import howtoShape1 from "@/shared/assets/howto-shape-1.svg";
import howtoShape2 from "@/shared/assets/howto-shape-2.svg";
import howtoShape3 from "@/shared/assets/howto-shape-3.svg";
import { cn } from "@/shared/lib/cn";
import { formatLongDate } from "@/shared/lib/date";
import { formatSum } from "@/shared/lib/money";
import { Button } from "@/shared/ui";
import { ProductGrid, ProductGridSkeleton, ProductRail } from "@/shared/ui/product";

/**
 * Figma "Main page". Desktop: hero, category tiles, popular products, how installment works, stats. Phones (D3,
 * docs/MOBILE_UI_PLAN.html B1): the shell's header with pinned search, the next payment for a customer with a
 * contract, a compact banner, a category rail, the installment hits rail, the ISHONCH promises and a product grid.
 */
export function HomePage() {
  return (
    <div className="flex flex-col gap-2 pb-6 lg:container-page lg:gap-20 lg:pt-4 lg:pb-0">
      <PaymentCard />
      <div className="container-page pt-2 lg:contents">
        <Hero />
      </div>
      <Categories />
      <Popular />
      <Promises />
      <div className="max-lg:hidden">
        <HowItWorks />
      </div>
      <div className="max-lg:hidden">
        <Stats />
      </div>
      <MoreProducts />
    </div>
  );
}

/** «Ваш платёж 499 167 сум · до 5 ноября» — only for a signed-in customer with something to pay (D37). */
function PaymentCard() {
  const { t, i18n } = useTranslation("catalog");
  const { status } = useSession();
  const { data } = useContractsSummary({
    query: { enabled: status === "authenticated", staleTime: 5 * 60_000 },
  });
  if (status !== "authenticated" || !data || data.cs_amount_due <= 0) return null;
  return (
    <div className="container-page pt-2 lg:hidden">
      <Link
        to="/cabinet/contracts?sheet=pay"
        className={cn(
          "flex items-center gap-3 rounded-card bg-surface p-3",
          data.cs_is_overdue && "ring-1 ring-danger/40 ring-inset",
        )}
      >
        <span className="flex min-w-0 flex-1 flex-col">
          <span className={cn("text-body-md", data.cs_is_overdue ? "text-danger" : "text-muted")}>
            {data.cs_is_overdue ? t("homePage.paymentOverdue") : t("homePage.payment")}
          </span>
          <span className="text-title-xs font-semibold text-ink-strong tabular-nums">
            {t("sum", { amount: formatSum(data.cs_amount_due) })}
            {data.cs_due_date && !data.cs_is_overdue && (
              <span className="font-normal text-muted">
                {" · "}
                {t("homePage.paymentDue", {
                  date: formatLongDate(data.cs_due_date, i18n.language),
                })}
              </span>
            )}
          </span>
        </span>
        <span className="rounded-lg2 bg-brand-600 px-3 py-2 text-body-lg font-medium text-white">
          {t("homePage.pay")}
        </span>
      </Link>
    </div>
  );
}

function Hero() {
  const { t } = useTranslation("catalog");
  return (
    <section className="relative isolate overflow-hidden rounded-card px-4 py-4 text-white bg-hero lg:min-h-[417px] lg:rounded-3xl lg:px-13 lg:py-18">
      <img
        src={heroShape1}
        alt=""
        aria-hidden
        className="absolute -top-14 right-0 -z-10 w-40 opacity-80 max-lg:hidden"
      />
      <img
        src={heroShape2}
        alt=""
        aria-hidden
        className="absolute bottom-6 left-[52%] -z-10 hidden w-32 opacity-70 lg:block"
      />
      <img
        src={heroShape3}
        alt=""
        aria-hidden
        className="absolute top-14 left-[42%] -z-10 hidden w-24 opacity-60 lg:block"
      />

      <div className="relative z-10 flex max-w-[58%] flex-col gap-2 lg:max-w-[645px] lg:gap-4">
        <span className="w-fit rounded-md bg-white/15 px-2 py-0.5 text-body-sm font-medium ring-1 ring-white/30">
          {t("hero.badge")}
        </span>
        <h1 className="text-title-md font-bold lg:text-heading-lg lg:tracking-[-0.02em]">
          {t("hero.title")}
        </h1>
        <p className="max-w-[460px] text-sm text-white/90 max-lg:hidden">{t("hero.text")}</p>
        <div className="mt-1 flex flex-wrap gap-3 lg:mt-4">
          <Button
            asChild
            size="sm"
            variant="secondary"
            className="bg-surface text-ink-strong hover:bg-canvas lg:h-11 lg:px-5"
          >
            <Link to="/installment">{t("hero.calculate")}</Link>
          </Button>
          <Button
            asChild
            className="bg-transparent ring-1 ring-white/70 hover:bg-white/10 max-lg:hidden"
          >
            <Link to="/catalog">{t("hero.browse")}</Link>
          </Button>
        </div>
      </div>
      <img
        src={heroAppliances}
        alt=""
        aria-hidden
        fetchPriority="high"
        width={635}
        height={380}
        className="pointer-events-none absolute right-[-6%] bottom-0 w-[58%] max-w-[260px] lg:right-4 lg:w-[47%] lg:max-w-[635px]"
      />
    </section>
  );
}

function Categories() {
  const { t } = useTranslation("catalog");
  const { data: categories = [] } = useCategories({ query: { staleTime: 5 * 60_000 } });
  const tiles = categories.flatMap((c) => c.pc_children).filter((c) => c.pc_is_shown_on_home);
  if (tiles.length === 0) return null;
  return (
    <>
      {/* phones: a rail of 72 px photo tiles, the last one opens the whole catalog */}
      <nav aria-label={t("homePage.categories")} className="bg-surface py-3 lg:hidden">
        <ul className="no-scrollbar flex gap-3 overflow-x-auto overscroll-x-contain px-3">
          {tiles.map((tile) => (
            <li key={tile.pc_id} className="w-21 shrink-0">
              <Link to={`/catalog/${tile.pc_slug}`} className="flex flex-col items-center gap-1.5">
                <span className="flex size-[72px] items-center justify-center overflow-hidden rounded-xl2 bg-canvas p-2">
                  {tile.pc_image_url && (
                    <img
                      src={tile.pc_image_url}
                      alt=""
                      loading="lazy"
                      className="size-full object-contain mix-blend-multiply"
                    />
                  )}
                </span>
                <span className="line-clamp-2 w-full text-center text-body-sm break-words hyphens-auto text-ink">
                  {tile.pc_name}
                </span>
              </Link>
            </li>
          ))}
          <li className="w-21 shrink-0">
            <Link to="/catalog" className="flex flex-col items-center gap-1.5">
              <span className="flex size-[72px] items-center justify-center rounded-xl2 bg-brand-50 text-brand-700">
                <LayoutGrid className="size-7" aria-hidden />
              </span>
              <span className="text-center text-body-sm text-ink">
                {t("homePage.allCategories")}
              </span>
            </Link>
          </li>
        </ul>
      </nav>
      {/* desktop: the Figma tiles */}
      <ul className="grid grid-cols-6 gap-4 max-lg:hidden">
        {tiles.map((tile) => (
          <li key={tile.pc_id}>
            <Link
              to={`/catalog/${tile.pc_slug}`}
              className="flex h-[220px] flex-col gap-3 rounded-card bg-canvas/75 p-3 transition-colors hover:bg-canvas"
            >
              <span className="text-sm font-semibold text-ink-strong">{tile.pc_name}</span>
              {tile.pc_image_url && (
                <img
                  src={tile.pc_image_url}
                  alt=""
                  loading="lazy"
                  className="min-h-0 flex-1 object-contain mix-blend-multiply"
                />
              )}
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}

function Popular() {
  const { t } = useTranslation("catalog");
  const favoriteIds = useFavoriteIdSet();
  const { data, isPending } = usePopular({ limit: 10 });
  const heart = (product: ProductCardResponse) => (
    <FavoriteButton product={product} isFavorite={favoriteIds.has(product.p_id)} />
  );
  return (
    <section className="flex flex-col gap-3 bg-surface py-4 lg:gap-6 lg:bg-transparent lg:py-0">
      <SectionTitle title={t("homePage.hits")} desktopTitle={t("popular")} to="/catalog" />
      <div className="container-page lg:contents">
        {isPending ? (
          <ProductGridSkeleton count={5} />
        ) : (
          <ProductRail
            products={data ?? []}
            columns={5}
            renderActions={heart}
            label={t("homePage.hits")}
          />
        )}
      </div>
    </section>
  );
}

/** The ISHONCH promises (approved by the owner): no down payment, a fast decision, stores nearby, free delivery. */
function Promises() {
  const { t } = useTranslation("catalog");
  const items: { icon: LucideIcon; title: string; text: string }[] = [
    { icon: BadgeCheck, title: t("promises.noDown"), text: t("promises.noDownText") },
    { icon: Clock, title: t("promises.fast"), text: t("promises.fastText") },
    { icon: Store, title: t("promises.stores"), text: t("promises.storesText") },
    { icon: Truck, title: t("promises.delivery"), text: t("promises.deliveryText") },
  ];
  return (
    <section aria-label={t("promises.title")} className="bg-surface px-3 py-4 lg:hidden">
      <ul className="grid grid-cols-2 gap-x-3 gap-y-4">
        {items.map(({ icon: Icon, title, text }) => (
          <li key={title} className="flex items-start gap-2">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700">
              <Icon className="size-5" aria-hidden />
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="text-body-lg font-semibold text-ink-strong">{title}</span>
              <span className="text-body-md text-muted">{text}</span>
            </span>
          </li>
        ))}
      </ul>
      <Link
        to="/installment"
        className="mt-4 flex items-center justify-center gap-1 rounded-xl bg-canvas py-3 text-body-lg font-medium text-brand-700"
      >
        {t("promises.how")}
        <ChevronRight className="size-4" aria-hidden />
      </Link>
    </section>
  );
}

/** Phones: more products after the rails, as a 2-column grid. */
function MoreProducts() {
  const { t } = useTranslation("catalog");
  const favoriteIds = useFavoriteIdSet();
  const { data, isPending } = useProducts({ sort: "-popularity", page: 1, limit: 20 });
  return (
    <section className="flex flex-col gap-3 pt-2 lg:hidden">
      <SectionTitle title={t("homePage.more")} to="/catalog" />
      <div className="container-page">
        {isPending ? (
          <ProductGridSkeleton count={4} />
        ) : (
          <ProductGrid
            products={data?.items ?? []}
            renderActions={(product) => (
              <FavoriteButton product={product} isFavorite={favoriteIds.has(product.p_id)} />
            )}
          />
        )}
      </div>
      <div className="container-page">
        <Button asChild variant="outline-neutral" className="w-full bg-surface">
          <Link to="/catalog">
            {t("showAll")}
            <ArrowRight className="size-4" aria-hidden />
          </Link>
        </Button>
      </div>
    </section>
  );
}

function SectionTitle({
  title,
  desktopTitle,
  to,
}: {
  title: string;
  desktopTitle?: string;
  to: string;
}) {
  const { t } = useTranslation("catalog");
  return (
    <div className="container-page flex items-center justify-between gap-3 lg:contents">
      <h2 className="text-title-sm font-semibold text-ink-strong lg:text-title-lg">
        <span className="lg:hidden">{title}</span>
        <span className="max-lg:hidden">{desktopTitle ?? title}</span>
      </h2>
      <Link to={to} className="flex items-center text-body-lg font-medium text-brand-700 lg:hidden">
        {t("homePage.all")}
        <ChevronRight className="size-4" aria-hidden />
      </Link>
    </div>
  );
}

function HowItWorks() {
  const { t } = useTranslation("catalog");
  const steps = [
    { title: t("howItWorks.step1"), text: t("howItWorks.step1Text"), shape: howtoShape1 },
    { title: t("howItWorks.step2"), text: t("howItWorks.step2Text"), shape: howtoShape2 },
    { title: t("howItWorks.step3"), text: t("howItWorks.step3Text"), shape: howtoShape3 },
  ];
  return (
    <section className="relative overflow-hidden rounded-3xl px-10 py-16 text-white bg-howto">
      <h2 className="mb-8 text-title-lg font-semibold">{t("howItWorks.title")}</h2>
      <ol className="grid grid-cols-3 gap-4">
        {steps.map((step, index) => (
          <li
            key={step.title}
            className="relative flex min-h-48 flex-col gap-3 overflow-hidden rounded-2xl border border-white/20 bg-white/10 p-4 backdrop-blur-md"
          >
            <h3 className="text-xl font-semibold">{step.title}</h3>
            <p className="max-w-64 text-sm text-white/85">{step.text}</p>
            <span className="mt-auto text-4xl font-bold">0{index + 1}</span>
            <img src={step.shape} alt="" aria-hidden className="absolute right-0 bottom-0 w-32" />
          </li>
        ))}
      </ol>
    </section>
  );
}

function Stats() {
  const { t } = useTranslation("catalog");
  const stats = [
    { value: "+120", label: t("stats.stores") },
    { value: "+7 000", label: t("stats.products") },
    { value: t("stats.free"), label: t("stats.freeText") },
  ];
  return (
    <section className="flex flex-col gap-6">
      <h2 className="text-title-lg font-semibold text-ink-strong">{t("stats.title")}</h2>
      <ul className="grid grid-cols-3 gap-4">
        {stats.map((stat) => (
          <li
            key={stat.label}
            className="relative flex flex-col items-center gap-1 overflow-hidden rounded-2xl bg-canvas px-4 py-8 text-center"
          >
            <span className="text-heading-xs font-semibold text-ink-strong">{stat.value}</span>
            <span className="text-sm text-ink">{stat.label}</span>
            <span aria-hidden className="absolute inset-x-0 bottom-0 h-1.5 bg-brand-500" />
          </li>
        ))}
      </ul>
    </section>
  );
}
